/**
 * Camera motion constants for the mission map (VLP-2300 WP-B). FIELD_UX_SPEC §5.6: no Live transition
 * longer than 200 ms, no decorative motion; reduced motion → instant (jumpTo / spring response 0).
 */

/** View-mode and focus transitions (≤ 200 ms in Live). */
export const CAMERA_TRANSITION_MS = 200;
/** Pitch the 3D view opens at. */
export const PITCH_3D = 55;
/** Chase follow pitch in 3D. */
export const CHASE_PITCH = 60;
/** Maximum pitch allowed in 3D. */
export const MAX_PITCH_3D = 70;
/** Follow smoothing time constants (s): centre, pitch, bearing. */
export const FOLLOW_TAU = { center: 0.22, pitch: 0.35, bearing: 0.6 } as const;
/** Spring glide of the aircraft symbols (shared closed-form spring, critically damped). */
export const GLIDE_RESPONSE_SEC = 0.4;
/** How long the hollow focus ghost stays (ms). */
export const FOCUS_GHOST_MS = 3000;

/**
 * cubic-bezier(x1, y1, x2, y2) as an easing function — used for `--ease-spring`
 * (cubic-bezier(0.32, 0.72, 0, 1), tokens.css) so map camera motion matches the CSS motion.
 */
export function cubicBezier(x1: number, y1: number, x2: number, y2: number): (t: number) => number {
  const cx = 3 * x1;
  const bx = 3 * (x2 - x1) - cx;
  const ax = 1 - cx - bx;
  const cy = 3 * y1;
  const by = 3 * (y2 - y1) - cy;
  const ay = 1 - cy - by;
  const sx = (t: number) => ((ax * t + bx) * t + cx) * t;
  const sy = (t: number) => ((ay * t + by) * t + cy) * t;
  const dx = (t: number) => (3 * ax * t + 2 * bx) * t + cx;
  return (x: number) => {
    if (x <= 0) return 0;
    if (x >= 1) return 1;
    let t = x;
    for (let i = 0; i < 8; i++) {
      const e = sx(t) - x;
      if (Math.abs(e) < 1e-6) break;
      const d = dx(t);
      if (Math.abs(d) < 1e-6) break;
      t -= e / d;
    }
    // bisection fallback keeps it monotone for any control points
    if (Math.abs(sx(t) - x) > 1e-4) {
      let lo = 0;
      let hi = 1;
      t = x;
      for (let i = 0; i < 30; i++) {
        if (sx(t) < x) lo = t;
        else hi = t;
        t = (lo + hi) / 2;
      }
    }
    return sy(t);
  };
}

/** --ease-spring from tokens.css. */
export const easeSpring = cubicBezier(0.32, 0.72, 0, 1);

export function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}
